import React, { useState, useEffect } from 'react';
import { 
  FaTimes, FaBuilding, FaUsers, FaCalendar, 
  FaInfoCircle, FaSpinner, FaSave
} from 'react-icons/fa';
import './EditRequestModal.css';

const EditRequestModal = ({ 
  isOpen, 
  onClose, 
  request, 
  departments, 
  onSubmit 
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    department_id: '',
    number_of_employees: 1,
    duration_days: 30,
    job_description: '',
    requirements: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && request) {
      setFormData({
        department_id: request.department_id || '',
        number_of_employees: request.number_of_employees || 1,
        duration_days: request.duration_days || 30,
        job_description: request.job_description || '',
        requirements: request.requirements || ''
      });
      setErrors({});
    }
  }, [isOpen, request]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.department_id) {
      newErrors.department_id = 'Please select a department';
    }

    if (formData.number_of_employees < 1 || formData.number_of_employees > 50) {
      newErrors.number_of_employees = 'Number of employees must be between 1 and 50';
    }

    if (formData.duration_days < 7 || formData.duration_days > 365) {
      newErrors.duration_days = 'Duration must be between 7 and 365 days';
    }

    if (!formData.job_description.trim()) {
      newErrors.job_description = 'Please provide a job description';
    }

    

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const updateData = {
      ...formData
    };

    try {
      await onSubmit(request.id, updateData);
      onClose();
    } catch (error) {
      console.error('Error updating request:', error);
    } finally {
      setLoading(false);
    }
  };

  // const calculateTotalAmount = () => {
  //   const baseRate = 10000;
  //   const employees = formData.number_of_employees || 0;
  //   const duration = formData.duration_days || 0;
  //   const months = duration / 30;
    
  //   return Math.round(baseRate * employees * months);
  // };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  if (!isOpen || !request) return null;

  return (
    <div className="edit-modal-overlay">
      <div className="edit-modal">
        <div className="edit-modal-header">
          <div className="edit-modal-header-content">
            <h2>Edit Request</h2>
            <p>Update your request details</p>
          </div>
          <button className="edit-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-form">
          <div className="edit-form-sections">
            {/* Current Status */}
            <div className="status-section">
              <div className="status-item">
                <span>Request ID:</span>
                <strong>REQ-{request.id}</strong>
              </div>
              <div className="status-item">
                <span>Current Status:</span>
                <span className={`status-badge status-${request.status}`}>
                  {request.status}
                </span>
              </div>
              <div className="status-item">
                <span>Created:</span>
                <strong>{new Date(request.created_at).toLocaleDateString()}</strong>
              </div>
            </div>

            {/* Edit Form */}
            <div className="form-section">
              <h3><FaBuilding /> Request Details</h3>
              
              <div className="form-group">
                <label htmlFor="department_id">
                  Department/Skill Required *
                </label>
                <select
                  id="department_id"
                  name="department_id"
                  value={formData.department_id}
                  onChange={handleChange}
                  className={errors.department_id ? 'error' : ''}
                  disabled={request.status !== 'pending'}
                >
                  <option value="">Select a department</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                {errors.department_id && (
                  <span className="error-message">{errors.department_id}</span>
                )}
                {request.status !== 'pending' && (
                  <div className="field-disabled-note">
                    Cannot change department for {request.status} requests
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="number_of_employees">
                    <FaUsers /> Number of Employees *
                  </label>
                  <input
                    type="number"
                    id="number_of_employees"
                    name="number_of_employees"
                    value={formData.number_of_employees}
                    onChange={handleChange}
                    min="1"
                    max="50"
                    className={errors.number_of_employees ? 'error' : ''}
                    disabled={request.status !== 'pending'}
                  />
                  {errors.number_of_employees && (
                    <span className="error-message">{errors.number_of_employees}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="duration_days">
                    <FaCalendar /> Duration (Days) *
                  </label>
                  <input
                    type="number"
                    id="duration_days"
                    name="duration_days"
                    value={formData.duration_days}
                    onChange={handleChange}
                    min="7"
                    max="365"
                    className={errors.duration_days ? 'error' : ''}
                    disabled={request.status !== 'pending'}
                  />
                  {errors.duration_days && (
                    <span className="error-message">{errors.duration_days}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3><FaInfoCircle /> Job Information</h3>
              
              <div className="form-group">
                <label htmlFor="job_description">
                  Job Description *
                </label>
                <textarea
                  id="job_description"
                  name="job_description"
                  value={formData.job_description}
                  onChange={handleChange}
                  rows="4"
                  className={errors.job_description ? 'error' : ''}
                  disabled={request.status !== 'pending'}
                />
                {errors.job_description && (
                  <span className="error-message">{errors.job_description}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="requirements">
                  Requirements & Qualifications
                </label>
                <textarea
                  id="requirements"
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleChange}
                  rows="3"
                  disabled={request.status !== 'pending'}
                />
              </div>
            </div>

            {/* <div className="form-section">
              <h3><FaMoneyBillWave /> Budget</h3>
              
              <div className="form-group">
                <label htmlFor="budget">
                  Budget per Employee (RWF) *
                </label>
                <div className="budget-input-group">
                  <span className="currency-prefix">RWF</span>
                  <input
                    type="number"
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    min="1000"
                    step="1000"
                    className={errors.budget ? 'error' : ''}
                    disabled={request.status !== 'pending'}
                  />
                </div>
                {errors.budget && (
                  <span className="error-message">{errors.budget}</span>
                )}
              </div>

              <div className="budget-summary">
                <div className="budget-summary-item">
                  <span>Estimated Total:</span>
                  <strong>{calculateTotalAmount().toLocaleString()} RWF</strong>
                </div>
                <div className="budget-disclaimer">
                  * Changes may affect the final amount
                </div>
              </div>
            </div>
          </div> */}
          </div>

          <div className="edit-modal-actions">
            <button 
              type="button" 
              className="edit-btn edit-btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="edit-btn edit-btn-save"
              disabled={loading || request.status !== 'pending'}
            >
              {loading ? (
                <>
                  <FaSpinner className="spinner" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRequestModal;